import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { format, parseISO } from 'date-fns';

import { http } from '@/api/http';
import { useAuth } from '@/features/auth/AuthProvider';

interface StatusCount {
  status: number;
  statusName: string;
  count: number;
}

interface DashboardSummary {
  totalContacts: number;
  newInPeriod: number;
  contactedInPeriod: number;
  totalCompanies: number;
  followUpsDueToday: number;
  followUpsOverdue: number;
  doNotContactCount: number;
  byStatus: StatusCount[];
}

interface TrendPoint {
  dateIst: string;
  created: number;
  contacted: number;
}

interface LeadSourceRow {
  leadSource: string;
  count: number;
  percentage: number;
}

/** Query keys for this feature, in one place so invalidation cannot typo. */
export const dashboardKeys = {
  all: ['dashboard'] as const,
  summary: () => [...dashboardKeys.all, 'summary'] as const,
  trend: (days: number) => [...dashboardKeys.all, 'trend', days] as const,
  leadSources: () => [...dashboardKeys.all, 'lead-sources'] as const,
};

/** A single headline figure. */
function StatCard({
  label,
  value,
  hint,
  emphasis,
}: {
  label: string;
  value: number | string;
  hint?: string;
  emphasis?: 'warning' | 'error';
}) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 0.5 }}>{label}</Typography>

        <Typography
          sx={{
            fontSize: 30,
            fontWeight: 700,
            lineHeight: 1.15,
            color: emphasis ? `${emphasis}.main` : 'text.primary',
          }}
        >
          {value}
        </Typography>

        {hint && (
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>{hint}</Typography>
        )}
      </CardContent>
    </Card>
  );
}

/** The landing screen. */
export function DashboardPage() {
  const theme = useTheme();
  const { user } = useAuth();

  const summary = useQuery({
    queryKey: dashboardKeys.summary(),
    queryFn: async () => (await http.get<DashboardSummary>('/dashboard/summary')).data,
  });

  const trend = useQuery({
    queryKey: dashboardKeys.trend(30),
    queryFn: async () =>
      (await http.get<TrendPoint[]>('/dashboard/contacts-trend', { params: { days: 30 } })).data,
  });

  const leadSources = useQuery({
    queryKey: dashboardKeys.leadSources(),
    queryFn: async () => (await http.get<LeadSourceRow[]>('/dashboard/lead-sources')).data,
  });

  if (summary.isPending) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  const data = summary.data;

  // Dates arrive as IST calendar dates from the API and are displayed as-is. No
  // client-side timezone conversion anywhere on this page — the server already
  // did the IST bucketing, and re-interpreting the values here would shift them
  // for anyone whose machine is not on IST.
  const chartData =
    trend.data?.map((point) => ({
      ...point,
      label: format(parseISO(point.dateIst), 'd MMM'),
    })) ?? [];

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h1">
          {greeting()}, {user?.fullName?.split(' ')[0]}
        </Typography>
        <Typography color="text.secondary">Here is where things stand today.</Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard
            label="Total contacts"
            value={data?.totalContacts ?? 0}
            hint={`${data?.newInPeriod ?? 0} added in the last 30 days`}
          />
        </Grid>

        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard label="Companies" value={data?.totalCompanies ?? 0} />
        </Grid>

        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard
            label="Follow-ups due today"
            value={data?.followUpsDueToday ?? 0}
            emphasis={data?.followUpsDueToday ? 'warning' : undefined}
          />
        </Grid>

        <Grid size={{ xs: 6, md: 3 }}>
          <StatCard
            label="Overdue"
            value={data?.followUpsOverdue ?? 0}
            emphasis={data?.followUpsOverdue ? 'error' : undefined}
            hint={data?.followUpsOverdue ? 'These need attention' : 'Nothing overdue'}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h4" sx={{ mb: 2 }}>
                Contacts added — last 30 days
              </Typography>

              <Box sx={{ height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                    <defs>
                      <linearGradient id="createdFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                      </linearGradient>
                    </defs>

                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />

                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                      tickLine={false}
                      axisLine={false}
                      // Every day is present in the data, including zeroes, so
                      // thinning the labels keeps the axis readable without
                      // distorting the spacing.
                      interval="preserveStartEnd"
                      minTickGap={24}
                    />

                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                      tickLine={false}
                      axisLine={false}
                      width={40}
                    />

                    <ChartTooltip
                      contentStyle={{
                        background: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                        fontSize: 13,
                      }}
                    />

                    <Area
                      type="monotone"
                      dataKey="created"
                      name="Added"
                      stroke={theme.palette.primary.main}
                      strokeWidth={2}
                      fill="url(#createdFill)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h4" sx={{ mb: 2 }}>
                By status
              </Typography>

              <Stack spacing={1.25}>
                {data?.byStatus.map((row) => (
                  <Stack
                    key={row.status}
                    direction="row"
                    sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <Typography sx={{ fontSize: 14 }}>{row.statusName}</Typography>
                    <Chip
                      label={row.count}
                      size="small"
                      variant={row.count ? 'filled' : 'outlined'}
                      color={row.count ? 'primary' : 'default'}
                    />
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h4" sx={{ mb: 2 }}>
            Where leads come from
          </Typography>

          {leadSources.data?.length ? (
            <Stack spacing={1.5}>
              {leadSources.data.map((row) => (
                <Box key={row.leadSource}>
                  <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: 14 }}>{row.leadSource}</Typography>
                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                      {row.count} · {row.percentage}%
                    </Typography>
                  </Stack>

                  <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'surfaceRaised' }}>
                    <Box
                      sx={{
                        height: '100%',
                        width: `${row.percentage}%`,
                        borderRadius: 3,
                        background: theme.palette.gradient,
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography color="text.secondary" sx={{ fontSize: 14 }}>
              No contacts yet in this period.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}

/** Greets by IST time of day, since that is where the team is. */
function greeting(): string {
  const istHour = Number(
    new Intl.DateTimeFormat('en-GB', {
      hour: 'numeric',
      hour12: false,
      timeZone: 'Asia/Kolkata',
    }).format(new Date()),
  );

  if (istHour < 12) return 'Good morning';
  if (istHour < 17) return 'Good afternoon';
  return 'Good evening';
}
